import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Pharmacogenomics series: CYP2D6 ultra-rapid metabolizer + CYP2C19 rapid metabolizer +
// SLC6A4 short/short explaining eight years of SSRI non-response.

export const antidepressantKofi: Scenario = {
  slug: 'antidepressant-kofi',
  series: 'Pharmacogenomics',
  title: "Eight years. Four SSRIs. None of them worked. Here's why.",
  socialHook:
    "Kofi, 36, was told his depression was treatment-resistant after four antidepressants failed. His genetics suggested a simpler story: the same dose was not behaving the same way in his body.",
  meta: {
    description:
      "A fictional case study: Kofi tried four SSRIs over eight years and got no relief. His results showed that standard doses were being cleared too quickly to work, while side effects appeared when doses were pushed higher.",
  },

  persona: {
    name: 'Kofi',
    age: 36,
    sex: 'Male',
    ancestry: 'Ghanaian-British',
    occupation: 'Secondary school teacher',
    chiefConcern:
      "Eight years of depression. He has tried sertraline, fluoxetine, citalopram, and escitalopram. None helped at standard doses, and two caused severe side effects when the dose was raised. He is on no antidepressant now because he gave up. His family doctor called it treatment-resistant depression. Kofi is not convinced.",
    familyHistory:
      "Mother: depression, on mirtazapine — works well for her. No one in the family has done well on SSRIs.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent low mood for eight years — not in crisis, but never well',
      'Low motivation and anhedonia — finds it hard to enjoy things he used to',
      'Fatigue disproportionate to activity level',
      'Social withdrawal — cancels plans, increasingly isolated',
    ],
    labs: [],
    medications: [
      'No current antidepressants — stopped after four failed SSRI trials',
      'Previous trials: sertraline (no effect at standard dose, side effects at higher dose)',
      'Previous trials: fluoxetine (no effect at standard dose)',
      'Previous trials: citalopram (no effect at standard dose)',
      'Previous trials: escitalopram (no effect at standard dose, side effects at higher dose)',
    ],
    supplements: ['None'],
    lifestyle: [
      'No substance use',
      'Non-smoker, minimal alcohol',
      'Sleep: 6–7 hours, unrefreshing, early waking',
      'Exercise: walking to and from school — no structured activity',
      'Diet: regular meals, no significant dietary restrictions',
    ],
  },

  genetics: [
    {
      gene: 'CYP2D6',
      variant: 'Ultra-rapid metabolizer',
      rsid: '*1xN / *2xN gene duplication',
      genotype: 'Gene duplication — ultra-rapid metabolizer',
      plainMeaning:
        "Kofi clears many antidepressants so quickly that a normal dose may not stay in his bloodstream long enough to help.",
      technicalDetail:
        "CYP2D6 is the liver enzyme that breaks down most SSRIs — including sertraline, fluoxetine, and paroxetine — and many SNRIs. Ultra-rapid metabolizers carry extra copies of the gene, producing far more enzyme than average. Drugs are broken down so fast that therapeutic blood levels are never reached at standard doses. A 50mg sertraline dose is metabolized and cleared before it can act. This also explains the 'intolerable side effects at higher dose' pattern: larger doses push CYP2D6 into saturation, creating unpredictable peaks instead of a steady therapeutic level.",
    },
    {
      gene: 'CYP2C19',
      variant: 'Rapid metabolizer',
      rsid: 'rs4244285 / rs4986893',
      genotype: 'Rapid metabolizer',
      plainMeaning:
        "Two of the antidepressants he tried — citalopram and escitalopram — are also cleared by a second pathway that runs fast for him.",
      technicalDetail:
        "CYP2C19 specifically metabolizes citalopram and escitalopram — two of Kofi's four SSRI trials. Rapid metabolizers clear these drugs faster than average, adding a second, independent mechanism of non-response on top of CYP2D6. His non-response on citalopram and escitalopram was driven by at least two genetic variants simultaneously.",
    },
    {
      gene: 'SLC6A4',
      variant: 'Short/short (5-HTTLPR)',
      rsid: 'rs25531 / insertion-deletion polymorphism',
      genotype: 'Two short alleles',
      plainMeaning:
        "Even if the dose were adjusted, SSRIs may still be a poor fit for Kofi. His mother's medication works through a different route, which may explain the family pattern.",
      technicalDetail:
        "SLC6A4 makes the protein that clears serotonin out of the synapse after it's been used. The 'short' variant is less efficient — serotonin lingers longer, and the system down-regulates over time. Having two short copies means his serotonin system is already compensated for reduced reuptake. SSRIs work by blocking that same reuptake step — meaning they may not be the right mechanism regardless of dose. Mirtazapine (his mother's drug) works through a completely different mechanism (NaSSA — blocking presynaptic alpha-2 receptors to increase release) and bypasses this entirely.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Four prescriptions told the same story",
      body: "Kofi is 36, a secondary school teacher, and has been depressed since his late twenties. He tried sertraline, fluoxetine, citalopram, and escitalopram. Each time, the standard dose did almost nothing. When two doses were raised, the side effects became too much. He stopped trying two years ago. Then his family doctor called the depression treatment-resistant. But one family clue kept bothering him: his mother does well on mirtazapine, and no one in the family seems to do well on SSRIs.",
    },
    {
      type: 'narrative',
      heading: "The label assumed the dose had a fair chance",
      body: "'Treatment-resistant depression' means depression that has not responded to two or more antidepressant trials. But the label assumes each drug reached the right level in the blood and stayed there long enough to work. If someone clears the drug unusually fast, a normal dose may never get a fair chance. The chart says the drug failed. The body may be saying the dose never behaved normally.",
    },
    {
      type: 'reveal',
      heading: "The same dose was not the same dose for him",
      body: "Kofi's results show that his body makes extra CYP2D6 enzyme, which breaks down many antidepressants. At standard doses, the medication may have cleared before it built up enough to help. When the dose was raised, the system may have become less predictable, creating spikes that felt like side effects instead of steady treatment. He may not have failed four medications. Four standard doses may have failed to match his biology.",
    },
    {
      type: 'reveal',
      heading: "The family clue finally mattered",
      body: "Two of his four trials — citalopram and escitalopram — also depend on CYP2C19, and Kofi clears that pathway quickly too. A third result points to a serotonin system that may not respond well to the SSRI mechanism even when dosing is solved. That makes his mother's response to mirtazapine more than a coincidence: it works through a different route and avoids the main problem pathways. The family pattern was a clue the whole time.",
    },
    {
      type: 'action',
      heading: "Five things to do with this information",
      items: [
        "Bring the pharmacogenomics results to a family doctor or psychiatrist who understands drug-gene interactions. A medication review is reasonable because these results can change prescribing decisions.",
        "Ask specifically about mirtazapine. It works differently from SSRIs and avoids the main pathways flagged in his results. His mother's strong response gives the conversation extra clinical context.",
        "If SSRIs are reconsidered, discuss blood-level monitoring. Therapeutic drug monitoring can show whether the medication ever reached a useful level in his bloodstream.",
        "Ask whether vortioxetine belongs in the discussion. It has a different pharmacological profile, but the decision should sit with a clinician who can weigh the full picture.",
        "Revisit the 'treatment-resistant' label. His depression may not have been adequately treated if the drugs never reached the right level or mechanism for him.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Kofi's 14-day check-in on mirtazapine",
      goalLabel: 'Daily mood, sleep & motivation — on mirtazapine',
      caption:
        "Kofi started mirtazapine after reviewing his pharmacogenomics results with his clinician. The first few days were sedating, which is common. By day 9, the grogginess eased and the mood shift was clear.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 2 },
          ],
          note: "Very sedated. Slept heavily but feel groggy this morning.",
        },
        {
          day: 2,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 2 },
          ],
          note: "Still sedated. Hard to get up. Sleep is the best it's been in years though.",
        },
        {
          day: 3,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Sleep', value: 9 },
            { label: 'Motivation', value: 3 },
          ],
          note: "Sedation still there but I'm adjusting. Mood hasn't moved yet.",
          milestone: "Sedation easing",
        },
        {
          day: 4,
          scores: [
            { label: 'Mood', value: 4 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 3 },
          ],
          note: "Less groggy in the mornings. Still flat but not struggling to get up.",
        },
        {
          day: 5,
          scores: [
            { label: 'Mood', value: 4 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 4 },
          ],
          note: "Managed a full day at work without feeling like I was running on empty.",
        },
        {
          day: 6,
          scores: [
            { label: 'Mood', value: 4 },
            { label: 'Sleep', value: 7 },
            { label: 'Motivation', value: 4 },
          ],
          note: "Appetite is stronger than usual. Prepared an actual meal tonight.",
        },
        {
          day: 7,
          scores: [
            { label: 'Mood', value: 5 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 4 },
          ],
          note: "Something is different. Still not 'good' but not dragging in the same way.",
        },
        {
          day: 8,
          scores: [
            { label: 'Mood', value: 5 },
            { label: 'Sleep', value: 7 },
            { label: 'Motivation', value: 5 },
          ],
          note: "Marked some homework. That hasn't happened at home in months.",
        },
        {
          day: 9,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 6 },
          ],
          note: "First day I wanted to go for a walk. Didn't plan it — just went.",
          milestone: "First day I wanted to go for a walk",
        },
        {
          day: 10,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 6 },
          ],
          note: "Called my mum. First time in weeks. Told her I'd started mirtazapine.",
        },
        {
          day: 11,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 6 },
          ],
          note: "Good lesson today — first time I've enjoyed teaching in a while.",
        },
        {
          day: 12,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 7 },
          ],
          note: "Went to the gym. I used to go regularly — haven't been in over a year.",
        },
        {
          day: 13,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Sleep', value: 7 },
            { label: 'Motivation', value: 6 },
          ],
          note: "Dip today — stressful school day. But it passed. I didn't spiral.",
        },
        {
          day: 14,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Sleep', value: 8 },
            { label: 'Motivation', value: 7 },
          ],
          note: "Two weeks in. This is the best two-week stretch I can remember in eight years.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'antidepressant-kofi',
    label: 'Explore your antidepressant & CYP metabolism genetics in Coach',
  },
}
